// infoQuestionForm.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from "reactstrap";
import classnames from "classnames";
import { withTranslation } from "react-i18next";

import Select from 'react-select';
import axiosInstance from "utils/axiosInstance";

// Child components
import AffectedAttributesList from "./AffectedAttributesList";
import VerifyRuleAIList from "./VerifyRuleAIList";
import AutoResizeInput from "../../components/AutoResizeInput";
import { scheduleResizeAll } from "../../components/useAutoResize"; // Adjust the path accordingly

// Tabs components
import InfoQuestionDependenciesTab from "./InfoQuestionDependenciesTab";
import InfoQuestionRolesTab from "./InfoQuestionRolesTab";


const InfoQuestionForm = ({ t, infoQuestion, setInfoQuestion, prevQuestions, onSave, onSaveRoles }) => {


  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("dependencies");
  /*
  const toggleTab = (tabId) => {
    if (activeTab !== tabId) { setActiveTab(tabId); }
  };
  */
  const toggleTab = (tabId) => {
  if (activeTab !== tabId) {
    setActiveTab(tabId);
    // Schedule a resize after a brief delay to ensure content is rendered
    setTimeout(() => {
      requestAnimationFrame(scheduleResizeAll);
    }, 0);
  }
};

  const [isSavingActivationConditionSets, setIsSavingActivationConditionSets] = useState(false);
  const [isSavedActivationConditionSets, setIsSavedActivationConditionSets] = useState(true);
  const [isSavingAffectedAttribute, setIsSavingAffectedAttribute] = useState(false);
  const [isSavedAffectedAttribute, setIsSavedAffectedAttribute] = useState(true);
  const [isSavingVerifyRuleAI, setIsSavingVerifyRuleAI] = useState(false);
  const [isSavedVerifyRuleAI, setIsSavedVerifyRuleAI] = useState(true);


  //--------------------------------------------------
  // Editing Question label
  //--------------------------------------------------

  const [isSavingLabel, setIsSavingLabel] = useState(false);
  const [isSavedLabel, setIsSavedLabel] = useState(true);
  const handleLabelChange = (e) => {
    setInfoQuestion({ ...infoQuestion, label: e.target.value });
    setIsSavedLabel(false);
  };
  const handleSaveLabel = async () => {

    setIsSavingLabel(true);
    const payload = { label: infoQuestion.label.trim() };
    if(!infoQuestion.label.trim().length){ return; }

    try {
      await onSave(payload);
      setIsSavedLabel(true);
    } catch (error) {
      // TODO: show error message
      console.error(error);
    } finally {
      setIsSavingLabel(false);
    }

  };


  //--------------------------------------------------
  // Editing Question Status
  //--------------------------------------------------

  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isSavedStatus, setIsSavedStatus] = useState(true);

  const statusOptions = [
    { value: 'needs_edit', label: '需要編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (questionId, newStatus) => {
    try {
      setIsSavingStatus(true);
      setIsSavedStatus(false);

      const res = await axiosInstance.patch(`/knovia/infoQuestion/${questionId}/updateEntryStatus`, {
        status: newStatus,
        type: 'question',
      });

      // Update local state
      setInfoQuestion(prev => ({ ...prev, status: newStatus }));

      setIsSavedStatus(true);

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update question status.");
    } finally {
      setIsSavingStatus(false);
    }
  };


  //--------------------------------------------------
  // Editing Required Prev Question
  //--------------------------------------------------
  const [isSavingPrevQuestion, setIsSavingPrevQuestion] = useState(false);
  const [isSavedPrevQuestion, setIsSavedPrevQuestion] = useState(true);

  const [selectedRequiredPrevQuestion, setSelectedRequiredPrevQuestion] = useState('');
  useEffect(()=>{
    console.log('infoQuestion:', infoQuestion);
    if(infoQuestion?.dependencies?.requiredQuestionIds?.length){
      console.log('infoQuestion?.dependencies?.requiredQuestionIds?.length:', infoQuestion?.dependencies?.requiredQuestionIds?.length);
      console.log('infoQuestion?.dependencies?.requiredQuestionIds[0]:', infoQuestion?.dependencies?.requiredQuestionIds[0]);
      setSelectedRequiredPrevQuestion(infoQuestion?.dependencies?.requiredQuestionIds[0]);
    }
  },[infoQuestion])

  const handlePrevQuestionChange = async (questionId, newPrevQuestion) => {
    try {
      setIsSavingPrevQuestion(true);
      setIsSavedPrevQuestion(false);

      const res = await axiosInstance.patch(`/knovia/infoQuestion/${questionId}/updatePrevQuestion`, {
        requiredQuestionId: newPrevQuestion
      });

      // Update local state
      setInfoQuestion(prev => ({ ...prev, 
        dependencies: { ...prev.dependencies, requiredQuestionIds: newPrevQuestion ? [newPrevQuestion] : [] }
      }));

      setSelectedRequiredPrevQuestion(newPrevQuestion);
      setIsSavedPrevQuestion(true);

    } catch (err) {
      console.error('Failed to update required prev question:', err);
      setError("Failed to update required prev question.");
    } finally {
      setIsSavingPrevQuestion(false);
    }
  }


  //--------------------------------------------------
  // Editing Reference Question
  //--------------------------------------------------

  const [selectedReferencePrevQuestion, setSelectedReferencePrevQuestion] = useState('');
  useEffect(()=>{
    if(infoQuestion?.dependencies?.refQuestionIds?.length){
      setSelectedReferencePrevQuestion(infoQuestion?.dependencies?.refQuestionIds[0]);
    }
  },[infoQuestion])

  const handleRefQuestionChange = async (questionId, newRefQuestion) => {
    try {

      setIsSavingVerifyRuleAI(true);
      setIsSavedVerifyRuleAI(false);

      const res = await axiosInstance.patch(`/knovia/infoQuestion/${questionId}/updateRefQuestion`, {
        refQuestionId: newRefQuestion
      });

      // Update local state
      setInfoQuestion(prev => ({ ...prev, 
        dependencies: { ...prev.dependencies, refQuestionIds: newRefQuestion ? [newRefQuestion] : [] }
      }));

      setSelectedReferencePrevQuestion(newRefQuestion);
      setIsSavedVerifyRuleAI(true);

    } catch (err) {
      console.error('Failed to update reference prev question:', err);
      setError("Failed to update reference prev question.");
    } finally {
      setIsSavingVerifyRuleAI(false);
    }
  }

  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <Container fluid>

      {/* 1) Label */}
      <Row className="mb-3">
        <Col xs={12}>
          <Row>
            {/* label and Input */}
            <Col xs={10} md={8}>
              <Row>
                <Col xs={12} md={4}>
                  <h3>問題文本:</h3>
                </Col>
                <Col xs={12} md={8}>
                  <AutoResizeInput
                    type="textarea"
                    rows={1}
                    value={infoQuestion.label}
                    onChange={handleLabelChange}
                    onBlur={()=>{
                      if(isSavedActivationConditionSets && isSavedAffectedAttribute && isSavedVerifyRuleAI){
                        handleSaveLabel();
                      }
                    }}
                  />
                </Col>
              </Row>
            </Col>

            {/* Check button */}
            <Col xs={2} md={4}>
              <Button color="outline-success" onClick={handleSaveLabel}
                style={{ padding: "7px 8px" }}
              >
                <i className="bx bx-check"/>
              </Button>
            </Col>
          </Row>

          {/* Show label saving status */}
          <Row>
            {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
            {!isSavingLabel && 
              <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
            }
          </Row>

        </Col>
      </Row>


      <Row className="mb-3">
        <Col xs={12}>
          <Row>
            {/* label and Input */}
            <Col xs={10} md={8}>
              <Row>
                <Col xs={12} md={4}>
                  <h3>Status:</h3>
                </Col>
                <Col xs={12} md={8}>
                  <Select
                    value={statusOptions.find(opt => opt.value === infoQuestion.status)}
                    onChange={(selected) => handleStatusChange(infoQuestion._id, selected.value)}
                    options={statusOptions}
                    isSearchable={false}
                    menuPlacement="auto"
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={2} md={4}></Col>
          </Row>

          {/* Show label saving status */}
          <Row>
            {isSavingStatus && <span style={{ color: "#f1b34c" }}>Saving...</span>}
            {!isSavingStatus && 
              <span style={{ color: isSavedStatus ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
            }
          </Row>

        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Row>
            {/* label and Input */}
            <Col xs={10} md={8}>
              <Row>
                <Col xs={12} md={4}>
                  <h3>需先填寫:</h3>
                </Col>
                <Col xs={12} md={8}>
                  <Select
                    value={[
                      { value: '', label: '— No required question —' },
                      ...prevQuestions
                        .map(q => ({
                          value: q._id,
                          label: `${q.order}. ${q.label}`
                        }))
                    ].find(option => option.value === selectedRequiredPrevQuestion)}
                    onChange={(selected) => handlePrevQuestionChange(infoQuestion._id, selected.value)}
                    options={[
                      { value: '', label: '— No required question —' },
                      ...prevQuestions
                        .map(q => ({
                          value: q._id,
                          label: `${q.order}. ${q.label}`
                        }))
                    ]}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                        zIndex: 9999, // ensure it's above modal
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                        color: '#495057',
                      }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                        color: 'black',
                      }),
                    }}
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={2} md={4}></Col>
          </Row>

          {/* Show label saving status */}
          <Row>
            {isSavingPrevQuestion && <span style={{ color: "#f1b34c" }}>Saving...</span>}
            {!isSavingPrevQuestion && 
              <span style={{ color: isSavedPrevQuestion ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
            }
          </Row>

        </Col>
      </Row>

      <hr style={{ borderTop: '1px solid black', margin: "2rem 0.5rem 3rem 0.5rem"}} />

      {/* --- Tabs --- */}
      <Nav tabs style={{ marginTop: "2rem", marginBottom: "20px" }}>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "dependencies" })}
            onClick={() => toggleTab("dependencies")}
            style={{ cursor: "pointer" }}
          >
            顯示條件 / 影響屬性 / 驗證規則
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "roles" })}
            onClick={() => toggleTab("roles")}
            style={{ cursor: "pointer" }}
          >
            檢查點 / 目標
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="dependencies">
          <InfoQuestionDependenciesTab
            infoQuestion={infoQuestion}
            setInfoQuestion={setInfoQuestion}
            prevQuestions={prevQuestions}
            selectedReferencePrevQuestion={selectedReferencePrevQuestion}
            handleRefQuestionChange={handleRefQuestionChange}
            onSave={onSave}

            isSavingActivationConditionSets={isSavingActivationConditionSets}
            setIsSavingActivationConditionSets={setIsSavingActivationConditionSets}
            isSavedActivationConditionSets={isSavedActivationConditionSets}
            setIsSavedActivationConditionSets={setIsSavedActivationConditionSets}
            isSavingAffectedAttribute={isSavingAffectedAttribute}
            setIsSavingAffectedAttribute={setIsSavingAffectedAttribute}
            isSavedAffectedAttribute={isSavedAffectedAttribute}
            setIsSavedAffectedAttribute={setIsSavedAffectedAttribute}
            isSavingVerifyRuleAI={isSavingVerifyRuleAI}
            setIsSavingVerifyRuleAI={setIsSavingVerifyRuleAI}
            isSavedVerifyRuleAI={isSavedVerifyRuleAI}
            setIsSavedVerifyRuleAI={setIsSavedVerifyRuleAI}
          />
        </TabPane>
        <TabPane tabId="roles">
          <InfoQuestionRolesTab
            infoQuestion={infoQuestion}
            setInfoQuestion={setInfoQuestion}
            onSaveRoles={onSaveRoles}
          />
        </TabPane>
      </TabContent>
        
    </Container>
  );
};

export default withTranslation()(InfoQuestionForm);
